import api from './api'

export type Direction = 'credit' | 'debit'
export type ReviewStatus = 'auto_match' | 'suggested_match' | 'manual_review'

export interface AppTransaction {
  id: string
  date: string
  description: string
  amount: number
  type?: Direction
  category_name?: string
  cost_type?: 'fixed' | 'variable'
}

export interface AppInvoice {
  id: string
  type: 'emessa' | 'ricevuta'
  number?: string
  date: string
  due_date?: string
  counterparty_name?: string
  total_amount: number
  payment_status: string
  paid_amount?: number
  notes?: string
}

export interface HistoricalMatchSignal {
  counterparty_name?: string
  transaction_description?: string
  historical_matching_frequency?: number
  counterparty_recurrence?: number
}

export interface ReconciliationTransactionInput {
  transaction_id: string
  workspace_id: string
  date: string
  amount: number
  description: string
  direction: Direction
  counterparty_name?: string
  is_recurring?: boolean
  metadata?: Record<string, unknown>
}

export interface ReconciliationInvoiceInput {
  invoice_id: string
  workspace_id: string
  invoice_number?: string
  issue_date: string
  due_date?: string
  amount: number
  direction: Direction
  counterparty_name?: string
  status: string
  metadata?: Record<string, unknown>
}

export interface ReconciliationRequestPayload {
  transactions: ReconciliationTransactionInput[]
  invoices: ReconciliationInvoiceInput[]
  historical_signals?: HistoricalMatchSignal[]
  auto_match_threshold?: number
  suggest_threshold?: number
  model_version?: string
}

export interface CanonicalReconciliationRequestPayload {
  transaction_limit?: number
  invoice_limit?: number
  auto_match_threshold?: number
  suggest_threshold?: number
  model_version?: string
  ensure_backfill?: boolean
}

export interface ReconciliationFeatureVector {
  amount_delta: number
  amount_proximity: number
  date_delta: number
  text_similarity: number
  iban_exactness: number
  counterparty_recurrence: number
  invoice_amount_proximity: number
  direction_coherence: number
  expected_payment_window: number
  historical_matching_frequency: number
  recurring_pattern_match: number
}

export interface ReconciliationSuggestion {
  transaction_id: string
  invoice_id: string
  rank: number
  score: number
  confidence: number
  status: ReviewStatus
  features: ReconciliationFeatureVector
  user_explanation: string
  audit_explanation: string
  model_version: string
  generated_at: string
}

export interface ReconciliationResponse {
  request_id: string
  workspace_id: string
  suggestions: ReconciliationSuggestion[]
  model_version: string
  generated_at: string
  warnings: string[]
}

export interface FutureCashEvent {
  event_id: string
  date: string
  amount: number
  direction: Direction
  event_type: string
  description: string
  confidence?: number
}

export interface DailyCashObservation {
  date: string
  balance: number
  inflows: number
  outflows: number
  anomalies_count?: number
  open_invoices_amount?: number
  tax_due_amount?: number
  payroll_marker?: number
  rent_marker?: number
  utilities_marker?: number
}

export interface CashflowForecastRequestPayload {
  observations: DailyCashObservation[]
  future_events?: FutureCashEvent[]
  horizons_days?: number[]
  use_runpod_tft?: boolean
  model_version?: string
}

export interface CanonicalCashflowForecastRequestPayload {
  lookback_days?: number
  horizons_days?: number[]
  use_runpod_tft?: boolean
  model_version?: string
  ensure_backfill?: boolean
}

export interface ForecastHorizonResult {
  horizon_days: number
  projected_balance: number
  lower_bound: number
  upper_bound: number
  confidence: number
  drivers: string[]
  needs_review: boolean
}

export interface CashflowForecastResponse {
  request_id: string
  workspace_id: string
  model_version: string
  generated_at: string
  horizons: ForecastHorizonResult[]
  baseline_used: boolean
  warnings: string[]
}

export interface FinancialStatementKpi {
  name: string
  value: number
  previous_value?: number
  yoy_change?: number
  unit?: string
}

export interface RetrievalSnippet {
  snippet_id: string
  source: string
  content: string
  score: number
}

export interface FinancialStatementRequestPayload {
  company_name: string
  period_label: string
  executive_kpis: FinancialStatementKpi[]
  technical_kpis: FinancialStatementKpi[]
  alert_flags?: string[]
  retrieval_context?: RetrievalSnippet[]
  model_version?: string
}

export interface StatementSection {
  title: string
  bullets: string[]
}

export interface FinancialStatementResponse {
  request_id: string
  workspace_id: string
  company_name: string
  period_label: string
  executive_summary: StatementSection
  accountant_analysis: StatementSection
  warning_signals: string[]
  key_strengths: string[]
  recommended_checks: string[]
  facts_used: string[]
  inferences: string[]
  confidence: number
  model_version: string
  generated_at: string
  needs_review: boolean
  warnings: string[]
}

export interface FinancialAIAuditEnvelope {
  module: string
  model_version: string
  confidence: number
  status: string
  needs_review: boolean
  input_summary: Record<string, unknown>
  output_summary: Record<string, unknown>
  warnings: string[]
  request_id: string
  generated_at: string
}

export interface FinancialAIEnvelope<TData> {
  request_id: string
  data: TData
  audit: FinancialAIAuditEnvelope
}

export interface ReconciliationLookup {
  transactions: Record<string, string>
  invoices: Record<string, string>
}

export type GovernanceReviewActionType = 'claim' | 'resolve' | 'approve' | 'reject' | 'dismiss'

export interface GovernanceReviewQueueItem {
  queue_item_id: string
  request_id: string
  workspace_id: string
  module_name: string
  subject_type: string
  subject_id: string
  secondary_subject_id?: string | null
  queue_status: string
  priority: string
  reason_codes: string[]
  input_summary: Record<string, unknown>
  output_summary: Record<string, unknown>
  recommended_action?: string | null
  assigned_to?: string | null
  claimed_by?: string | null
  created_by?: string | null
  metadata: Record<string, unknown>
  latest_action_type?: string | null
  latest_action_at?: string | null
  latest_action_by?: string | null
  action_count: number
  created_at: string
  claimed_at?: string | null
  resolved_at?: string | null
}

export interface GovernanceReviewQueueSummary {
  total_items: number
  pending_count: number
  claimed_count: number
  resolved_count: number
  rejected_count: number
  dismissed_count: number
  high_priority_open_count: number
  oldest_pending_created_at?: string | null
}

export interface GovernanceReviewQueueData {
  summary: GovernanceReviewQueueSummary
  items: GovernanceReviewQueueItem[]
  filters: Record<string, unknown>
}

export interface GovernanceDeploymentSnapshot {
  deployment_id: string
  model_key: string
  model_version: string
  runtime_target: string
  deployment_status: string
  allowlisted_workspaces: string[]
  metadata: Record<string, unknown>
  deployed_by?: string | null
  deployed_at: string
}

export interface GovernanceRolloutMetricSnapshot {
  metric_name: string
  metric_value: number
  recorded_at: string
  metadata: Record<string, unknown>
}

export interface GovernanceRolloutStateItem {
  rollout_id: string
  module_name: string
  environment: string
  rollout_mode: string
  champion_model_key?: string | null
  champion_model_version?: string | null
  challenger_model_key?: string | null
  challenger_model_version?: string | null
  allowlisted_workspaces: string[]
  metadata: Record<string, unknown>
  created_by?: string | null
  created_at: string
  deployments: GovernanceDeploymentSnapshot[]
  recent_metrics: GovernanceRolloutMetricSnapshot[]
}

export interface GovernanceRolloutSummary {
  total_rollouts: number
  static_count: number
  shadow_count: number
  pilot_count: number
  modules: string[]
}

export interface GovernanceRolloutData {
  summary: GovernanceRolloutSummary
  items: GovernanceRolloutStateItem[]
  filters: Record<string, unknown>
}

export interface GovernanceModuleHealth {
  module_name: string
  rollout_mode?: string | null
  open_reviews: number
  pending_reviews: number
  claimed_reviews: number
  high_priority_open_reviews: number
  avg_latency_ms?: number | null
  p95_latency_ms?: number | null
  max_latency_ms?: number | null
  avg_confidence?: number | null
  review_required_count: number
  total_cost: number
  cost_event_count: number
  open_drift_reports: number
  high_severity_drift_reports: number
}

export interface GovernanceAlert {
  severity: string
  category: string
  module_name?: string | null
  title: string
  description: string
}

export interface GovernanceObservabilityOverall {
  open_reviews: number
  high_priority_open_reviews: number
  avg_latency_ms?: number | null
  p95_latency_ms?: number | null
  total_cost: number
  open_drift_reports: number
  high_severity_drift_reports: number
}

export interface GovernanceCostByProvider {
  provider: string
  total_cost: number
  event_count: number
}

export interface GovernanceDriftReportSummary {
  drift_report_id: string
  module_name: string
  model_key?: string | null
  model_version?: string | null
  workspace_id?: string | null
  severity: string
  status: string
  report_window_start?: string | null
  report_window_end?: string | null
  signals: Record<string, unknown>
  metadata: Record<string, unknown>
  created_at: string
}

export interface GovernanceObservabilityData {
  generated_at: string
  window_hours: number
  overall: GovernanceObservabilityOverall
  module_health: GovernanceModuleHealth[]
  alerts: GovernanceAlert[]
  cost_by_provider: GovernanceCostByProvider[]
  recent_drift_reports: GovernanceDriftReportSummary[]
  filters: Record<string, unknown>
}

interface GovernanceApiEnvelope<TData> {
  request_id?: string
  data: TData
  audit?: Record<string, unknown>
}

const VALID_STATUSES = new Set(['pending', 'partial', 'overdue', 'paid'])

function toDateValue(value?: string): number {
  if (!value) return Number.NEGATIVE_INFINITY
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed
}

function roundNumber(value: number, digits = 2): number {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

function deriveDirection(transaction: AppTransaction): Direction {
  if (transaction.type) {
    return transaction.type
  }
  return transaction.amount >= 0 ? 'credit' : 'debit'
}

function computeYoYChange(value: number, previousValue?: number): number | undefined {
  if (previousValue === undefined || previousValue === 0) {
    return undefined
  }
  return roundNumber(((value - previousValue) / Math.abs(previousValue)) * 100, 1)
}

export function buildReconciliationLookup(
  transactions: AppTransaction[],
  invoices: AppInvoice[],
): ReconciliationLookup {
  return {
    transactions: Object.fromEntries(
      transactions.map((transaction) => [
        transaction.id,
        transaction.description || `Movimento ${transaction.id}`,
      ]),
    ),
    invoices: Object.fromEntries(
      invoices.map((invoice) => [
        invoice.id,
        invoice.counterparty_name || invoice.number || `Fattura ${invoice.id}`,
      ]),
    ),
  }
}

export function buildReconciliationRequest(
  workspaceId: string,
  transactions: AppTransaction[],
  invoices: AppInvoice[],
): ReconciliationRequestPayload | null {
  const normalizedTransactions = [...transactions]
    .filter((transaction) => Number.isFinite(transaction.amount) && transaction.date)
    .sort((left, right) => toDateValue(right.date) - toDateValue(left.date))
    .slice(0, 80)
    .map<ReconciliationTransactionInput>((transaction) => ({
      transaction_id: transaction.id,
      workspace_id: workspaceId,
      date: transaction.date,
      amount: roundNumber(Math.abs(transaction.amount)),
      description: transaction.description,
      direction: deriveDirection(transaction),
      counterparty_name: undefined,
      is_recurring: transaction.cost_type === 'fixed',
      metadata: {
        category_name: transaction.category_name,
        source: 'frontend-dashboard',
      },
    }))

  const normalizedInvoices = [...invoices]
    .filter((invoice) => Number.isFinite(invoice.total_amount) && invoice.date)
    .filter((invoice) => invoice.payment_status !== 'paid')
    .sort((left, right) => toDateValue(right.due_date || right.date) - toDateValue(left.due_date || left.date))
    .slice(0, 40)
    .map<ReconciliationInvoiceInput>((invoice) => ({
      invoice_id: invoice.id,
      workspace_id: workspaceId,
      invoice_number: invoice.number,
      issue_date: invoice.date,
      due_date: invoice.due_date,
      amount: roundNumber(Math.abs(invoice.total_amount)),
      direction: invoice.type === 'emessa' ? 'credit' : 'debit',
      counterparty_name: invoice.counterparty_name,
      status: VALID_STATUSES.has(invoice.payment_status) ? invoice.payment_status : 'pending',
      metadata: {
        paid_amount: invoice.paid_amount ?? 0,
        notes: invoice.notes,
      },
    }))

  if (normalizedTransactions.length === 0 || normalizedInvoices.length === 0) {
    return null
  }

  return {
    transactions: normalizedTransactions,
    invoices: normalizedInvoices,
    historical_signals: [],
    auto_match_threshold: 0.92,
    suggest_threshold: 0.72,
    model_version: 'lightgbm-v0',
  }
}

export function buildCashflowRequest(
  transactions: AppTransaction[],
  invoices: AppInvoice[],
  currentBalance?: number,
  horizonsDays: number[] = [7, 30, 60, 90],
): CashflowForecastRequestPayload | null {
  const cutoff = Date.now() - 120 * 24 * 60 * 60 * 1000
  const recentTransactions = [...transactions]
    .filter((transaction) => Number.isFinite(transaction.amount) && transaction.date)
    .filter((transaction) => {
      const parsed = Date.parse(transaction.date)
      return !Number.isNaN(parsed) && parsed >= cutoff
    })
    .sort((left, right) => toDateValue(left.date) - toDateValue(right.date))

  if (recentTransactions.length === 0) {
    return null
  }

  const unresolvedInvoices = invoices.filter((invoice) => invoice.payment_status !== 'paid')
  const unresolvedAmount = unresolvedInvoices.reduce((sum, invoice) => sum + Math.abs(invoice.total_amount), 0)

  const byDay = new Map<
    string,
    {
      inflows: number
      outflows: number
      payroll_marker: number
      rent_marker: number
      utilities_marker: number
    }
  >()

  for (const transaction of recentTransactions) {
    const dateKey = transaction.date
    const entry = byDay.get(dateKey) ?? {
      inflows: 0,
      outflows: 0,
      payroll_marker: 0,
      rent_marker: 0,
      utilities_marker: 0,
    }

    const amountAbs = Math.abs(transaction.amount)
    const direction = deriveDirection(transaction)
    if (direction === 'credit') {
      entry.inflows += amountAbs
    } else {
      entry.outflows += amountAbs
    }

    const text = `${transaction.description} ${transaction.category_name ?? ''}`.toLowerCase()
    if (text.includes('stipend') || text.includes('payroll') || text.includes('salary')) {
      entry.payroll_marker = 1
    }
    if (text.includes('rent') || text.includes('affitto') || text.includes('lease')) {
      entry.rent_marker = 1
    }
    if (text.includes('utility') || text.includes('energia') || text.includes('luce') || text.includes('gas')) {
      entry.utilities_marker = 1
    }

    byDay.set(dateKey, entry)
  }

  const orderedDays = Array.from(byDay.entries()).sort(([left], [right]) => toDateValue(left) - toDateValue(right))
  const totalNet = orderedDays.reduce((sum, [, values]) => sum + values.inflows - values.outflows, 0)
  const endingBalance = Number.isFinite(currentBalance) ? Number(currentBalance) : 0
  let runningBalance = endingBalance - totalNet

  const observations = orderedDays.map<DailyCashObservation>(([date, values]) => {
    runningBalance += values.inflows - values.outflows
    return {
      date,
      balance: roundNumber(runningBalance),
      inflows: roundNumber(values.inflows),
      outflows: roundNumber(values.outflows),
      anomalies_count: 0,
      open_invoices_amount: roundNumber(unresolvedAmount),
      tax_due_amount: 0,
      payroll_marker: values.payroll_marker,
      rent_marker: values.rent_marker,
      utilities_marker: values.utilities_marker,
    }
  })

  const today = new Date().toISOString().slice(0, 10)
  const futureEvents = unresolvedInvoices
    .filter((invoice) => invoice.due_date && invoice.due_date >= today)
    .sort((left, right) => toDateValue(left.due_date) - toDateValue(right.due_date))
    .slice(0, 20)
    .map<FutureCashEvent>((invoice) => ({
      event_id: invoice.id,
      date: invoice.due_date!,
      amount: roundNumber(Math.abs(invoice.total_amount)),
      direction: invoice.type === 'emessa' ? 'credit' : 'debit',
      event_type: invoice.type === 'emessa' ? 'invoice_collection' : 'invoice_payment',
      description: invoice.counterparty_name || invoice.number || `Invoice ${invoice.id}`,
      confidence: invoice.payment_status === 'overdue' ? 0.95 : 0.8,
    }))

  return {
    observations,
    future_events: futureEvents,
    horizons_days: horizonsDays,
    use_runpod_tft: true,
    model_version: 'tft-v0',
  }
}

export function createStatementKpi(
  name: string,
  value: number,
  previousValue?: number,
  unit = 'EUR',
): FinancialStatementKpi {
  return {
    name,
    value: roundNumber(value),
    previous_value: previousValue !== undefined ? roundNumber(previousValue) : undefined,
    yoy_change: computeYoYChange(value, previousValue),
    unit,
  }
}

export function buildStatementRequest(params: {
  companyName: string
  periodLabel: string
  executiveKpis: FinancialStatementKpi[]
  technicalKpis: FinancialStatementKpi[]
  alertFlags?: string[]
  retrievalContext?: RetrievalSnippet[]
}): FinancialStatementRequestPayload | null {
  const executive_kpis = params.executiveKpis.filter((kpi) => Number.isFinite(kpi.value))
  const technical_kpis = params.technicalKpis.filter((kpi) => Number.isFinite(kpi.value))

  if (!params.companyName || !params.periodLabel || (executive_kpis.length === 0 && technical_kpis.length === 0)) {
    return null
  }

  return {
    company_name: params.companyName,
    period_label: params.periodLabel,
    executive_kpis,
    technical_kpis,
    alert_flags: params.alertFlags ?? [],
    retrieval_context: params.retrievalContext ?? [],
    model_version: 'deepseek-r1-v0',
  }
}

export const financialIntelligenceService = {
  async getReconciliationSuggestions(payload: ReconciliationRequestPayload) {
    const response = await api.post<FinancialAIEnvelope<ReconciliationResponse>>(
      '/financial-ai/reconciliation/suggestions',
      payload,
    )
    return response.data
  },

  async getCanonicalReconciliationSuggestions(
    payload: CanonicalReconciliationRequestPayload = {},
  ) {
    const response = await api.post<FinancialAIEnvelope<ReconciliationResponse>>(
      '/financial-ai/reconciliation/suggestions/canonical',
      payload,
    )
    return response.data
  },

  async getCashflowForecast(payload: CashflowForecastRequestPayload) {
    const response = await api.post<FinancialAIEnvelope<CashflowForecastResponse>>(
      '/financial-ai/cashflow/forecast',
      payload,
    )
    return response.data
  },

  async getCanonicalCashflowForecast(
    payload: CanonicalCashflowForecastRequestPayload = {},
  ) {
    const response = await api.post<FinancialAIEnvelope<CashflowForecastResponse>>(
      '/financial-ai/cashflow/forecast/canonical',
      payload,
    )
    return response.data
  },

  async analyzeFinancialStatements(payload: FinancialStatementRequestPayload) {
    const response = await api.post<FinancialAIEnvelope<FinancialStatementResponse>>(
      '/financial-ai/financial-statements/analyze',
      payload,
    )
    return response.data
  },

  async getGovernanceReviewQueue(params: {
    module_name?: string
    queue_status?: string
    limit?: number
  } = {}) {
    const response = await api.get<GovernanceApiEnvelope<GovernanceReviewQueueData>>(
      '/financial-ai/admin/reviews',
      { params },
    )
    return response.data.data
  },

  async claimReviewItem(payload: {
    queue_item_id: string
    request_id: string
    module_name: string
    notes?: string
    metadata?: Record<string, unknown>
  }) {
    const response = await api.post<GovernanceApiEnvelope<Record<string, unknown>>>(
      '/financial-ai/admin/reviews/claim',
      payload,
    )
    return response.data.data
  },

  async resolveReviewItem(payload: {
    queue_item_id: string
    request_id: string
    module_name: string
    action_type: GovernanceReviewActionType
    notes?: string
    corrected_value?: Record<string, unknown>
    metadata?: Record<string, unknown>
  }) {
    const response = await api.post<GovernanceApiEnvelope<Record<string, unknown>>>(
      '/financial-ai/admin/reviews/resolve',
      payload,
    )
    return response.data.data
  },

  async getGovernanceRollouts(params: {
    module_name?: string
    environment?: string
  } = {}) {
    const response = await api.get<GovernanceApiEnvelope<GovernanceRolloutData>>(
      '/financial-ai/admin/rollout',
      { params },
    )
    return response.data.data
  },

  async activateRollout(payload: {
    module_name: string
    champion_model_key: string
    champion_model_version: string
    challenger_model_key?: string
    challenger_model_version?: string
    rollout_mode: string
    allowlisted_workspaces?: string[]
    metadata?: Record<string, unknown>
  }) {
    const response = await api.post<GovernanceApiEnvelope<Record<string, unknown>>>(
      '/financial-ai/admin/rollout/activate',
      payload,
    )
    return response.data.data
  },

  async deactivateRollout(payload: {
    module_name: string
    champion_model_key?: string | null
    champion_model_version?: string | null
  }) {
    const response = await api.post<GovernanceApiEnvelope<Record<string, unknown>>>(
      '/financial-ai/admin/rollout/deactivate',
      payload,
    )
    return response.data.data
  },

  async getGovernanceObservability(params: {
    module_name?: string
    window_hours?: number
  } = {}) {
    const response = await api.get<GovernanceApiEnvelope<GovernanceObservabilityData>>(
      '/financial-ai/admin/observability',
      { params },
    )
    return response.data.data
  },
}
